import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Permission } from '@/access-control.config';
import {
    clearTokens,
    getStoredTokens,
    loginWithCredentials,
    loginAsGuest,
    isGuestSession,
    logoutSession,
    refreshAuthorization,
    type LoginCredentials,
} from './session';
import { getSessionExpiredEventName, resetSessionExpiredNotification, type SessionExpiredDetail } from './session-events';
import { isAuthDisabled } from '@/chassis/core/auth/auth-config';

export type PermissionId = Permission | string;

export interface AuthUser {
    id: string;
    name: string;
    roles?: string[];
    email?: string;
    cpf?: string;
    systemAdmin?: boolean;
    representationTypes?: string[];
    /** Bug #92 (Flavio, 03/06/2026): ultimo card de representacao usado por
     *  esta conta. Vem do login response. Pode ser 'citizen-view',
     *  'admin-view' ou um UUID de representacao. null em primeiro login. */
    lastSelectedRepresentationId?: string | null;
    [key: string]: unknown;
}

export interface AuthSnapshot {
    user: AuthUser | null;
    permissions: PermissionId[];
}

export type ViewMode = 'full' | 'citizen';

export interface AuthContextValue {
    user: AuthUser | null;
    permissions: Set<PermissionId>;
    hasPermission: (permission: PermissionId | PermissionId[], mode?: 'any' | 'all') => boolean;
    setAuthSnapshot: (snapshot: AuthSnapshot) => void;
    resetAuth: () => void;
    status: 'idle' | 'loading' | 'ready' | 'error';
    error?: string | null;
    signIn: (credentials: LoginCredentials) => Promise<void>;
    signInAsGuest: (captchaToken: string) => Promise<void>;
    signOut: () => Promise<void>;
    isGuest: boolean;
    refresh: () => Promise<void>;
    /** Modo de visualizacao ativo — 'citizen' restringe permissoes a somente-leitura */
    viewMode: ViewMode;
    setViewMode: (mode: ViewMode) => void;
}

const LOCAL_STORAGE_KEY = 'cdcb.auth.snapshot';
const SESSION_EXPIRED_MESSAGE_KEY = 'cdcb.sessionExpiredMessage';
const SESSION_REDIRECT_PATH_KEY = 'cdcb.sessionRedirectPath';
const TECHNICAL_SESSION_MESSAGES = ['token não fornecido', 'sessão expirada ou inválida', 'unauthorized', 'forbidden'];

const DISABLED_MODE_SNAPSHOT: AuthSnapshot = {
    user: {
        id: 'auth-disabled-user',
        name: 'Auth Disabled',
    },
    permissions: [],
};

const defaultSnapshot: AuthSnapshot = {
    user: null,
    permissions: [],
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const permissionAliasMap: Record<string, PermissionId[]> = {
    'admin-area:write': ['user:update'],
};

function inferFallbackPermission(candidate: PermissionId, ownedPermissions: Set<PermissionId>): boolean {
    if (typeof candidate !== 'string') {
        return false;
    }

    const segments = candidate.split(':');
    const operation = segments.length > 1 ? segments[segments.length - 1] : '';

    if (!operation) {
        return false;
    }

    // Namespaces protegidos — exigem permissao explicita, nao conceder por fallback.
    // Sem isso, qualquer usuario autenticado veria o card "Administrador" no portal.
    const protectedNamespaces = ['admin-area'];
    const namespace = segments.length > 1 ? segments.slice(0, -1).join(':') : '';
    if (protectedNamespaces.includes(namespace)) {
        return false;
    }

    // Fallback de leitura: qualquer usuario autenticado pode ler recursos publicos
    if (ownedPermissions.has('user:read') || ownedPermissions.has('auth:login')) {
        if (operation === 'read' || operation === 'view') {
            return true;
        }
    }

    // Nao conceder :write/:manage por fallback — permissoes de escrita devem ser
    // atribuidas explicitamente via roles (PREGOEIRO, EQUIPE_APOIO, etc.)
    return false;
}

function readStoredSnapshot(): AuthSnapshot {
    if (typeof window === 'undefined') {
        return defaultSnapshot;
    }

    try {
        const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!raw) {
            return defaultSnapshot;
        }
        const parsed = JSON.parse(raw) as AuthSnapshot;
        if (!parsed || typeof parsed !== 'object') {
            return defaultSnapshot;
        }
        return {
            user: parsed.user ?? null,
            permissions: Array.isArray(parsed.permissions) ? parsed.permissions : [],
        };
    } catch (error) {
        console.warn('[AuthProvider] Failed to read stored snapshot:', error);
        return defaultSnapshot;
    }
}

function persistSnapshot(snapshot: AuthSnapshot) {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        // SMOKE-17 (2026-04-20): em snapshot logged-out (user=null sem
        // permissions), removemos a chave em vez de gravar o objeto vazio.
        // Antes, localStorage ficava com `cdcb.auth.snapshot =
        // {"user":null,"permissions":[]}` mesmo pós-logout, sinalizando falso
        // "sessão residual" para quem inspeciona o storage. Remove é mais
        // honesto — ausência significa "deslogado".
        const isEmpty = !snapshot?.user && (!snapshot?.permissions || snapshot.permissions.length === 0);
        if (isEmpty) {
            window.localStorage.removeItem(LOCAL_STORAGE_KEY);
        } else {
            window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(snapshot));
        }
    } catch (error) {
        console.warn('[AuthProvider] Failed to persist snapshot:', error);
    }
}

function shouldIgnoreSessionExpiredEvent(detail?: SessionExpiredDetail): boolean {
    const rawReason = typeof detail?.reason === 'string' ? detail.reason.trim().toLowerCase() : '';
    const path = (detail?.path ?? '').toLowerCase();

    const isApiKeyError =
        rawReason.includes('api key') ||
        rawReason.includes('x-api-key') ||
        rawReason.includes('apikey');

    return isApiKeyError || path.startsWith('/external/v1/');
}

function resolveSessionExpiredMessage(rawReason: string): string {
    const fallbackMessage = 'Sua sessão expirou. Faça login novamente.';
    const normalizedReason = rawReason.trim().toLowerCase();
    if (!normalizedReason) {
        return fallbackMessage;
    }
    const isTechnical = TECHNICAL_SESSION_MESSAGES.some((message) => normalizedReason.includes(message));
    return isTechnical ? fallbackMessage : rawReason;
}

function persistSessionExpiredRedirect(message: string) {
    const currentTarget = `${window.location.pathname ?? ''}${window.location.search ?? ''}${window.location.hash ?? ''}` || '/';
    const isUnauthorizedPage = currentTarget.startsWith('/unauthorized');

    window.sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_KEY, message);
    if (isUnauthorizedPage || !currentTarget || currentTarget === '/') {
        window.sessionStorage.removeItem(SESSION_REDIRECT_PATH_KEY);
        return;
    }
    window.sessionStorage.setItem(SESSION_REDIRECT_PATH_KEY, currentTarget);
}

function redirectToLoginWithSessionExpiredFlag() {
    try {
        const target = new URL(window.location.href);
        target.pathname = '/login';
        target.searchParams.set('sessionExpired', '1');
        window.location.replace(target.toString());
    } catch {
        window.location.replace('/login?sessionExpired=1');
    }
}

export interface AuthProviderProps {
    children: React.ReactNode;
    /** Useful for tests or server-side hydration */
    initialSnapshot?: AuthSnapshot;
}

export function AuthProvider({ children, initialSnapshot }: AuthProviderProps) {
    const authDisabled = isAuthDisabled();
    const baseSnapshot = useMemo(() => initialSnapshot ?? readStoredSnapshot(), [initialSnapshot]);
    const snapshot = authDisabled ? DISABLED_MODE_SNAPSHOT : baseSnapshot;
    const [user, setUser] = useState<AuthUser | null>(snapshot.user);
    const [permissions, setPermissions] = useState<Set<PermissionId>>(new Set(snapshot.permissions));
    const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(snapshot.user || authDisabled ? 'ready' : 'idle');
    const [error, setError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>('full');
    const sessionExpiryHandlingRef = useRef(false);

    const setAuthSnapshot = useCallback((next: AuthSnapshot) => {
        const resolved = authDisabled ? DISABLED_MODE_SNAPSHOT : next;
        setUser(resolved.user ?? null);
        setPermissions(new Set(resolved.permissions ?? []));
        if (!authDisabled) {
            persistSnapshot({
                user: resolved.user ?? null,
                permissions: Array.isArray(resolved.permissions) ? resolved.permissions : [],
            });
        }
        resetSessionExpiredNotification();
        setStatus('ready');
        setError(null);
    }, [authDisabled, resetSessionExpiredNotification]);

    const resetAuth = useCallback(() => {
        if (!authDisabled) {
            clearTokens();
        }
        setAuthSnapshot(authDisabled ? DISABLED_MODE_SNAPSHOT : defaultSnapshot);
        setStatus(authDisabled ? 'ready' : 'idle');
        setError(null);
    }, [authDisabled, setAuthSnapshot]);

    const matchesPermission = useCallback(
        (candidate: PermissionId) => {
            if (authDisabled) {
                return true;
            }

            // Em modo cidadao, restringir a somente-leitura — mesmo systemAdmin
            if (viewMode === 'citizen') {
                const segments = typeof candidate === 'string' ? candidate.split(':') : [];
                const operation = segments.length > 1 ? segments[segments.length - 1] : '';
                if (operation && operation !== 'read' && operation !== 'view') {
                    return false;
                }
            }

            if (user?.systemAdmin === true) {
                return true;
            }
            // Suporte a wildcard '*' — role SYSTEM_ADMIN pode ter permissions: ['*']
            if (permissions.has('*')) {
                return true;
            }
            if (permissions.has(candidate)) {
                return true;
            }
            const aliases = permissionAliasMap[candidate];
            if (aliases?.length) {
                return aliases.some((alias) => permissions.has(alias));
            }

            return inferFallbackPermission(candidate, permissions);
        },
        [authDisabled, permissions, user, viewMode]
    );

    const hasPermission = useCallback(
        (permission: PermissionId | PermissionId[], mode: 'any' | 'all' = 'any') => {
            if (authDisabled) {
                return true;
            }
            const list = Array.isArray(permission) ? permission : [permission];
            if (!list.length) {
                return true;
            }
            if (mode === 'all') {
                return list.every((item) => matchesPermission(item));
            }
            return list.some((item) => matchesPermission(item));
        },
        [authDisabled, matchesPermission]
    );

    const signIn = useCallback(async (credentials: LoginCredentials) => {
        if (authDisabled) {
            setAuthSnapshot(DISABLED_MODE_SNAPSHOT);
            setStatus('ready');
            setError(null);
            return;
        }

        setStatus('loading');
        setError(null);
        try {
            const { snapshot: nextSnapshot } = await loginWithCredentials(credentials);
            setAuthSnapshot(nextSnapshot);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Falha ao autenticar';
            setStatus('error');
            setError(message);
            throw err;
        }
    }, [authDisabled, setAuthSnapshot]);

    const signInAsGuest = useCallback(async (captchaToken: string) => {
        setStatus('loading');
        setError(null);
        try {
            const { snapshot: nextSnapshot } = await loginAsGuest(captchaToken);
            setAuthSnapshot(nextSnapshot);
            setViewMode('citizen');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Falha ao entrar como visitante';
            setStatus('error');
            setError(message);
            throw err;
        }
    }, [setAuthSnapshot]);

    const isGuest = useMemo(() => isGuestSession(), [user]);

    const signOut = useCallback(async () => {
        if (authDisabled) {
            resetAuth();
            return;
        }

        // SMOKE-17 fix (2026-04-20): limpar tokens e state ANTES da HTTP.
        // Antes, `await logoutSession()` bloqueava 4-7s esperando o POST
        // `/authentication/logout` completar (ou timeoutar). Durante essa
        // janela, `auth.token` ainda estava no localStorage e o usuário via
        // "logout não funciona" — UI ficava em `/logout` pending. Agora
        // resetAuth roda síncrono no início; a request vai como fire-and-
        // forget para o servidor invalidar a sessão em paralelo. Se falhar,
        // não importa para o cliente — os tokens já foram limpos localmente.
        resetAuth();
        setStatus('idle');
        void logoutSession().catch((error) => {
            console.warn('[AuthProvider] logoutSession (background) failed:', error);
        });
    }, [authDisabled, resetAuth]);

    const refresh = useCallback(async () => {
        if (authDisabled) {
            setStatus('ready');
            setError(null);
            return;
        }

        setStatus((current) => (current === 'ready' ? current : 'loading'));
        try {
            const snapshotFromBackend = await refreshAuthorization({ user, permissions: Array.from(permissions) });
            if (snapshotFromBackend) {
                setAuthSnapshot(snapshotFromBackend);
            }
            setStatus('ready');
            setError(null);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Falha ao sincronizar permissões';
            setStatus('error');
            setError(message);
            throw err;
        }
    }, [authDisabled, permissions, setAuthSnapshot, user]);

    useEffect(() => {
        if (authDisabled) {
            setStatus('ready');
            setError(null);
            return;
        }

        const tokens = getStoredTokens();
        if (!tokens?.accessToken) {
            // Do not trust cached snapshots without a valid access token
            setAuthSnapshot(defaultSnapshot);
            setStatus('ready');
            setError(null);
            return;
        }

        let cancelled = false;
        const sync = async () => {
            setStatus('loading');
            try {
                const refreshed = await refreshAuthorization(snapshot);
                if (cancelled) return;
                if (refreshed) {
                    setAuthSnapshot(refreshed);
                } else {
                    // Token rejected (401/403) or no longer valid — clear cached auth
                    setAuthSnapshot(defaultSnapshot);
                }
                setStatus('ready');
                setError(null);
            } catch (err) {
                if (cancelled) return;
                const message = err instanceof Error ? err.message : 'Falha ao carregar sessão';
                setStatus('error');
                setError(message);
            }
        };
        sync();

        return () => {
            cancelled = true;
        };
    }, [authDisabled, setAuthSnapshot, snapshot]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const eventName = getSessionExpiredEventName();
        const handleSessionExpired = (event: Event) => {
            if (sessionExpiryHandlingRef.current) {
                return;
            }

            const detail = (event as CustomEvent<SessionExpiredDetail>).detail;
            if (shouldIgnoreSessionExpiredEvent(detail)) {
                return;
            }

            sessionExpiryHandlingRef.current = true;

            const rawReason = typeof detail?.reason === 'string' ? detail.reason.trim() : '';
            const message = resolveSessionExpiredMessage(rawReason);

            try {
                persistSessionExpiredRedirect(message);
            } catch {
                // Ignora falhas de sessionStorage (quota indisponível, ambiente sem suporte etc.)
            }

            resetAuth();
            redirectToLoginWithSessionExpiredFlag();

            // Libera o handler após um pequeno atraso para permitir novas sessões
            window.setTimeout(() => {
                sessionExpiryHandlingRef.current = false;
            }, 1000);
        };

        window.addEventListener(eventName, handleSessionExpired as EventListener);
        return () => {
            window.removeEventListener(eventName, handleSessionExpired as EventListener);
            sessionExpiryHandlingRef.current = false;
        };
    }, [resetAuth]);

    const value = useMemo<AuthContextValue>(
        () => ({
            user,
            permissions,
            hasPermission,
            setAuthSnapshot,
            resetAuth,
            status,
            error,
            signIn,
            signInAsGuest,
            signOut,
            isGuest,
            refresh,
            viewMode,
            setViewMode,
        }),
        [error, hasPermission, isGuest, permissions, refresh, resetAuth, setAuthSnapshot, signIn, signInAsGuest, signOut, status, user, viewMode]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
